import React, {
  useState,
  useRef,
  useEffect,
  ReactElement,
  ReactNode,
  WeakValidationMap,
  isValidElement,
  ReactHTMLElement,
  Ref,
} from 'react';
import PropTypes from 'prop-types';
import { Overlay } from '@restart/ui';
import { CSSTransition } from 'react-transition-group';
import { forgeClassHelper } from '../utils/classes';
import { themeContextPropTypes, useTheme } from '../Root/ThemeContext';
import { Theme } from '../Root/context';
import { Placement } from '@restart/ui/usePopper';
import { PortalContextProps, withPortalData } from '../PortalProvider/PortalContext';
import { OverlayInjectedProps } from '@restart/ui/esm/Overlay';

const classes = forgeClassHelper({ name: 'tooltip', isPortal: true });

export type PlacementTypes =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'right-start'
  | 'right-end'
  | 'left-start'
  | 'left-end';

export interface TooltipProps {
  children: React.ReactNode;
  className?: string;
  id: string;
  placement?: PlacementTypes;
  showTip?: boolean;
  text: ReactNode;
  theme?: Theme;
  underline?: boolean;
  wrapperClassName?: string;
}

export interface TipProps extends Omit<OverlayInjectedProps, 'ref'> {
  // arrowProps is provided by <Overlay> not user input, so we can narrow the type as HTMLAttributes
  arrowProps?: React.HTMLAttributes<HTMLDivElement>;
  children?: React.ReactNode;
  className?: string;
  id: string;
  placement?: Placement;
  theme?: Theme;
}

interface FadeProps {
  children?: React.ReactNode;
}

const FastFade = ({ children, ...transitionProps }: FadeProps): ReactElement => {
  return (
    <CSSTransition timeout={200} classNames="fast-fade" {...transitionProps}>
      {children}
    </CSSTransition>
  );
};

const Tip = React.forwardRef(
  (
    { arrowProps, children, className, id, placement, theme, ...props }: TipProps,
    ref: Ref<HTMLDivElement>
  ): ReactElement => {
    return (
      <div ref={ref} {...classes({ states: placement, extra: className, theme })} {...props}>
        <div id={id} role="tooltip" {...classes({ element: 'body' })}>
          <div {...classes({ element: 'body-content' })}>{children}</div>
        </div>
        <div {...classes({ element: 'arrow' })} {...arrowProps}></div>
      </div>
    );
  }
);

Tip.displayName = 'Tip';

export type TooltipComponentProps = TooltipProps & PortalContextProps;

function Tooltip({
  children,
  className,
  wrapperClassName,
  id,
  placement = 'top',
  showTip,
  text,
  theme: upstreamTheme,
  underline = false,
  portalData: { portalNode },
  ...passedProps
}: TooltipComponentProps): ReactElement {
  // Default to false. Otherwise the Tooltip position will be incorrect on initial render.
  const [showTipControl, setShowTipControl] = useState<boolean | undefined>(false);
  const targetRef = useRef<HTMLSpanElement>(null);
  const theme = useTheme(upstreamTheme);
  useEffect(() => {
    setShowTipControl(showTip);
  }, [showTip]);

  function handleShow(): void {
    if (showTip === undefined) {
      setShowTipControl(true);
    }
  }
  function handleHide(): void {
    if (showTip === undefined) {
      setShowTipControl(false);
    }
  }

  return (
    <span
      {...classes({
        element: 'span',
        modifiers: { underline },
        extra: `${wrapperClassName ?? ''}`,
      })}
      ref={targetRef}
      onMouseEnter={handleShow}
      onFocus={handleShow}
      onMouseLeave={handleHide}
      onBlur={handleHide}
    >
      {React.Children.map(children, (child) => {
        if (isValidElement(child)) {
          return React.cloneElement(child as ReactHTMLElement<HTMLElement>, { 'aria-describedby': id });
        } else {
          return <span aria-describedby={id}>{child}</span>;
        }
      })}
      <Overlay
        show={showTipControl}
        flip={true}
        placement={placement}
        offset={[0, 10]}
        target={targetRef.current}
        transition={FastFade}
        {...passedProps}
        container={portalNode}
      >
        {(tooltipProps, { arrowProps, popper }) => {
          // {className, theme, id} are from <Tooltip />, otherwise props are from <Overlay />
          return (
            <Tip
              className={className}
              theme={theme}
              id={id}
              arrowProps={arrowProps}
              placement={popper?.placement}
              {...tooltipProps}
            >
              {text}
            </Tip>
          );
        }}
      </Overlay>
    </span>
  );
}

const tooltipPropTypes: WeakValidationMap<TooltipProps & { '...rest': unknown }> = {
  /** Used to associate the tooltip text with the child element */
  id: PropTypes.string.isRequired,
  /** The text displayed in the tooltip */
  text: PropTypes.node.isRequired,
  /** The target component, element, or text that will have the tooltip applied to it */
  children: PropTypes.node.isRequired,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** The placement of the tooltip around its target */
  placement: PropTypes.oneOf([
    'top',
    'bottom',
    'left',
    'right',
    'top-start',
    'top-end',
    'bottom-start',
    'bottom-end',
    'right-start',
    'right-end',
    'left-start',
    'left-end',
  ]),
  /** Controls whether the tooltip is visible. When `undefined` tooltip is visible on hover. */
  showTip: PropTypes.bool,
  /** Theme variables for configuring an instance of Forge. See Root component for more details. */
  theme: themeContextPropTypes,
  /** Underline the content getting a tooltip. */
  underline: PropTypes.bool,
  /** Adds a class to the outer element getting a tooltip. */
  wrapperClassName: PropTypes.string,
  /** Passthrough props are passed through to the @restart/ui [Overlay](https://react-restart.github.io/ui/Overlay) a third party component used to position the tooltip */
  '...rest': PropTypes.any,
};
Tooltip.propTypes = tooltipPropTypes;

export default withPortalData(Tooltip);
